import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FilePlus2, FileSpreadsheet, Download, ArrowLeft, Upload, Lock, UploadCloud, Pencil, Loader2, CheckCircle2 } from 'lucide-react';
import { languoidEnglishName } from '@kolectra/shared/languoids';
import { ChallengeState } from '@kolectra/shared/states';
import { Permission } from '@kolectra/shared/permissions';
import type { ChallengeSummary, ChallengeDesign } from '@kolectra/shared/schemas';
import type { RowResult } from '@kolectra/shared/import';
import { CardSwitch, useConfirm } from '@kolectra/ui';
import { useWebAuth, useWebPerms } from '../auth.js';
import { useLanguage } from '../language.js';
import { challengesApi, utterancesApi, importApi, exportApi, ApiError, newIdempotencyKey } from '../lib/api-client.js';
import { PageHead } from '../components/page-head.js';
import { SingleCreate, type CreatedInfo, type EditSeed } from './create-form.js';

/**
 * Challenge Creation (web, live). Author a single segment (full submission
 * format) or — only for a team with no segments yet — bulk-import a CSV. New
 * segments are created as DRAFT challenges (gated by a per-language threshold)
 * and published one or many via the `submit` transition. Drafts can be reopened
 * to view/edit (seeded from `GET /challenges/:id/design`).
 */

type Mode = 'choose' | 'single' | 'import' | 'export';

/** Max unpublished drafts per language before publishing is required (UI gate). */
const DRAFT_THRESHOLD = 5;

const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);
const toLevel = (v: unknown): 'HARD' | 'EXTRA_HARD' => (v === 'extra hard' ? 'EXTRA_HARD' : 'HARD');

/** Read a picked file (xlsx/csv) to base64 — via a data URL to handle binary safely. */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => {
      const s = String(fr.result);
      const comma = s.indexOf(',');
      resolve(comma >= 0 ? s.slice(comma + 1) : s);
    };
    fr.onerror = () => reject(fr.error ?? new Error('Could not read the file.'));
    fr.readAsDataURL(file);
  });
}

/** Decode a base64 string to an ArrayBuffer for a download Blob. */
function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const bin = atob(b64);
  const buf = new ArrayBuffer(bin.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < bin.length; i++) view[i] = bin.charCodeAt(i);
  return buf;
}

/** Next segment id from the highest existing one, same S### format. */
function nextSegmentId(challenges: ChallengeSummary[]): string {
  let max = 0;
  for (const c of challenges) {
    const m = /^S(\d{3})$/.exec(c.segmentId);
    if (m) max = Math.max(max, Number(m[1]));
  }
  return `S${String(max + 1).padStart(3, '0')}`;
}

type RawUtt = Record<string, unknown>;

/** Map the form's json_template record into the API's challenge + utterances. */
function recordToPayloads(record: object, segmentId: string, lang: string) {
  const seg = (record as { segment?: Record<string, unknown> }).segment ?? {};
  const utts: RawUtt[] = Array.isArray(seg.utterances) ? (seg.utterances as RawUtt[]) : [];

  // Utterances carry only the per-line translation/script (§0).
  const utterances = utts.map((u, i) => ({
    ordinal: i,
    speakerId: str(u.utterance_speaker, `U${String(i + 1).padStart(2, '0')}`),
    utteranceContext: str(u.utterance_context),
    text: str(u.text),
    registerLabel: str(u.register, 'mrk'),
    translationLevel: toLevel(u.translation_level),
    translationChallengeDescription: str(u.translation_challenge_description) || undefined,
    referenceTranslation: str(u.reference_translation) || undefined,
  }));

  const joinNonEmpty = (key: string) => utts.map((u) => str(u[key])).filter(Boolean).join('\n');
  const anyExtraHard = (key: string) => utts.some((u) => u[key] === 'extra hard');

  // The reasoning task (question/answer/level/description) is authored once at
  // the challenge level (§0); the translation gold is still derived from the
  // per-utterance lines so the challenge-level review/export stay coherent.
  const challenge = {
    segmentId,
    languoidCode: lang,
    domain: str(seg.domain),
    segmentContext: str(seg.segment_context),
    // Segment audio is one concatenated take per challenge (§0) → always SOLO.
    recordingFlow: 'SOLO' as const,
    questionText: str(seg.question),
    goldTranslation: joinNonEmpty('reference_translation'),
    goldAnswer: str(seg.reference_answer),
    translationLevel: anyExtraHard('translation_level') ? ('EXTRA_HARD' as const) : ('HARD' as const),
    reasoningLevel: seg.reasoning_level === 'extra hard' ? ('EXTRA_HARD' as const) : ('HARD' as const),
    translationChallengeDescription: joinNonEmpty('translation_challenge_description'),
    reasoningChallengeDescription: str(seg.reasoning_challenge_description),
  };

  return { challenge, utterances };
}

/** Rebuild a json_template record from a fetched design, to seed the edit form. */
function buildRecordFromDesign(d: ChallengeDesign): object {
  return {
    segment: {
      id: `${d.segmentId}_${d.languoidCode}`,
      lang: d.languoidCode,
      segment_context: d.segmentContext,
      domain: d.domain,
      // Challenge-level reasoning task (§0).
      question: d.questionText ?? '',
      reference_answer: d.goldAnswer,
      reasoning_level: d.reasoningLevel === 'EXTRA_HARD' ? 'extra hard' : 'hard',
      reasoning_challenge_description: d.reasoningChallengeDescription,
      utterances: d.utterances.map((u) => ({
        utterance_speaker: u.speakerId,
        utterance_context: u.utteranceContext,
        register: u.registerLabel,
        text: u.text,
        audio_url: '(pending recording)',
        translation_level: u.translationLevel === 'EXTRA_HARD' ? 'extra hard' : 'hard',
        translation_challenge_description: u.translationChallengeDescription ?? '',
        reference_translation: u.referenceTranslation ?? '',
      })),
    },
  };
}

export function CreationPage() {
  const { account } = useWebAuth();
  const perms = useWebPerms();
  const canExport = perms.has(Permission.CAN_EXPORT);
  const teamId = account.team.id;
  const { langs, lang, setLang } = useLanguage();
  const confirm = useConfirm();
  const qc = useQueryClient();

  const [mode, setMode] = useState<Mode>('choose');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formNonce, setFormNonce] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Selection is per-language; reset it when the language changes.
  useEffect(() => setSelected(new Set()), [lang]);

  const listQuery = useQuery({
    queryKey: ['challenges', teamId],
    queryFn: () => challengesApi.list(teamId),
  });
  const all = listQuery.data?.items ?? [];
  const drafts = all.filter((c) => c.state === ChallengeState.DRAFT);
  const langDrafts = drafts.filter((d) => d.languoidCode === lang);
  const overThreshold = langDrafts.length >= DRAFT_THRESHOLD;
  const nextId = nextSegmentId(all);
  const domainSuggestions = [...new Set(all.filter((c) => c.languoidCode === lang).map((c) => c.domain).filter(Boolean))];

  const designQuery = useQuery({
    queryKey: ['design', editingId],
    queryFn: () => challengesApi.getDesign(editingId!),
    enabled: !!editingId,
  });
  const editSeed: EditSeed | null =
    editingId && designQuery.data
      ? {
          id: designQuery.data.id,
          segmentId: designQuery.data.segmentId,
          languoidCode: designQuery.data.languoidCode,
          record: buildRecordFromDesign(designQuery.data),
        }
      : null;

  const createMut = useMutation({
    mutationFn: async (info: CreatedInfo) => {
      const { challenge, utterances } = recordToPayloads(info.record, nextId, lang);
      const created = await challengesApi.create(teamId, challenge, newIdempotencyKey());
      await utterancesApi.set(created.id, { utterances });
      return created;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['challenges', teamId] }),
  });

  const updateMut = useMutation({
    mutationFn: async (info: CreatedInfo) => {
      const { challenge, utterances } = recordToPayloads(info.record, '', lang);
      const { segmentId: _s, languoidCode: _l, ...patch } = challenge;
      await challengesApi.update(editingId!, patch);
      await utterancesApi.set(editingId!, { utterances });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['challenges', teamId] });
      void qc.invalidateQueries({ queryKey: ['design', editingId] });
    },
  });

  const publishMut = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) await challengesApi.submit(id, newIdempotencyKey());
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['challenges', teamId] });
      setSelected(new Set());
    },
  });

  const busy = createMut.isPending || updateMut.isPending;
  const submitError = createMut.error ?? updateMut.error;

  function startCreate() {
    setEditingId(null);
    setMode('single');
  }
  function startEdit(id: string) {
    setEditingId(id);
    setMode('single');
  }
  function backToList() {
    setEditingId(null);
    setMode('choose');
  }

  function handleSubmit(info: CreatedInfo) {
    if (editingId) {
      updateMut.mutate(info, {
        onSuccess: () => {
          setEditingId(null);
          setMode('choose');
        },
      });
      return;
    }
    const willHitLimit = langDrafts.length + 1 >= DRAFT_THRESHOLD;
    createMut.mutate(info, {
      onSuccess: () => {
        if (willHitLimit) setMode('choose');
        else setFormNonce((n) => n + 1);
      },
    });
  }

  const allChecked = langDrafts.length > 0 && langDrafts.every((d) => selected.has(d.id));
  const selectedCount = langDrafts.filter((d) => selected.has(d.id)).length;

  function toggleAll() {
    setSelected(allChecked ? new Set() : new Set(langDrafts.map((d) => d.id)));
  }
  function toggle(id: string) {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }
  async function publishSelected() {
    const ids = langDrafts.filter((d) => selected.has(d.id)).map((d) => d.id);
    if (ids.length === 0) return;
    if (
      await confirm({
        title: `Publish ${ids.length} segment${ids.length === 1 ? '' : 's'}?`,
        message: 'Published segments leave drafts and enter the recording pipeline.',
        confirmLabel: 'Publish',
      })
    ) {
      publishMut.mutate(ids);
    }
  }

  return (
    <>
      <PageHead
        crumb={`Create · ${account.team.name}`}
        title="Challenge creation"
        lead={
          <>
            Author a segment with every field of the submission format. New segments stay{' '}
            <b>drafts</b> for a second-look review — reopen to edit, and publish when ready.
          </>
        }
      />

      <div className="pad" style={{ maxWidth: 960 }}>
        {langs.length > 1 && mode === 'choose' && (
          <CardSwitch
            label="Viewing language"
            value={lang}
            onChange={setLang}
            options={langs.map((c) => ({
              value: c,
              label: languoidEnglishName(c) ?? c,
              sub: `${drafts.filter((d) => d.languoidCode === c).length} drafts`,
            }))}
          />
        )}

        {mode === 'choose' ? (
          <div className="makergrid" style={{ marginBottom: 28 }}>
            <button
              className="maker meta"
              disabled={overThreshold}
              onClick={startCreate}
              title={overThreshold ? `Publish some drafts first (limit ${DRAFT_THRESHOLD})` : undefined}
            >
              <div className="mi">
                {overThreshold ? <Lock size={20} strokeWidth={1.8} /> : <FilePlus2 size={20} strokeWidth={1.8} />}
              </div>
              <h3>Single segment</h3>
              <p>Fill in the segment context and each utterance’s gold, levels, and references — the full submission record.</p>
              <div className="makerfoot">
                {overThreshold ? `Draft limit reached (${DRAFT_THRESHOLD})` : `Next: ${nextId}_${lang} →`}
              </div>
            </button>

            <button className="maker data" onClick={() => setMode('import')}>
              <div className="mi">
                <FileSpreadsheet size={20} strokeWidth={1.8} />
              </div>
              <h3>Import a spreadsheet</h3>
              <p>Bulk-create or sync a whole prep sheet at once. Re-upload the same file anytime — unchanged segments are skipped, edited ones updated.</p>
              <div className="makerfoot">CSV · review then commit →</div>
            </button>

            <button
              className="maker data"
              disabled={!canExport}
              onClick={() => setMode('export')}
              title={canExport ? undefined : 'Needs the export permission'}
            >
              <div className="mi">
                {canExport ? <Download size={20} strokeWidth={1.8} /> : <Lock size={20} strokeWidth={1.8} />}
              </div>
              <h3>Export a spreadsheet</h3>
              <p>Download the team’s challenges as a prep sheet — the same CSV format the importer reads. Re-import it anywhere.</p>
              <div className="makerfoot">{canExport ? 'CSV · published or all →' : 'Unavailable — needs export rights'}</div>
            </button>
          </div>
        ) : mode === 'single' ? (
          <div style={{ marginBottom: 28 }}>
            <button className="btn ghost sm" onClick={backToList} style={{ marginBottom: 14 }}>
              <ArrowLeft size={14} strokeWidth={2} /> All options
            </button>
            {submitError && (
              <div className="banner bad" style={{ marginBottom: 12 }}>
                {submitError instanceof ApiError ? submitError.message : 'Couldn’t save the segment.'}
              </div>
            )}
            {busy && (
              <div className="banner info" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Loader2 size={14} className="spin" /> Saving…
              </div>
            )}
            {editingId && designQuery.isLoading ? (
              <div className="banner info" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Loader2 size={14} className="spin" /> Loading the draft…
              </div>
            ) : editingId && designQuery.isError ? (
              <div className="banner bad">Couldn’t load this draft.</div>
            ) : (
              <SingleCreate
                key={editingId ? `edit-${editingId}` : `new-${formNonce}`}
                segmentId={nextId}
                lang={lang}
                domainSuggestions={domainSuggestions}
                edit={editSeed}
                onSubmit={handleSubmit}
                onCancel={backToList}
              />
            )}
          </div>
        ) : mode === 'import' ? (
          <div style={{ marginBottom: 28 }}>
            <button className="btn ghost sm" onClick={backToList} style={{ marginBottom: 14 }}>
              <ArrowLeft size={14} strokeWidth={2} /> All options
            </button>
            <ImportPanel teamId={teamId} onCommitted={backToList} />
          </div>
        ) : (
          <div style={{ marginBottom: 28 }}>
            <button className="btn ghost sm" onClick={backToList} style={{ marginBottom: 14 }}>
              <ArrowLeft size={14} strokeWidth={2} /> All options
            </button>
            <ExportPanel teamId={teamId} />
          </div>
        )}

        {overThreshold && mode === 'choose' && (
          <div className="banner warn" style={{ marginBottom: 16 }}>
            You’ve reached the draft limit ({DRAFT_THRESHOLD}) for {languoidEnglishName(lang) ?? lang}. Publish
            some below before creating more.
          </div>
        )}

        <div className="toolbar" style={{ marginBottom: 12 }}>
          <strong style={{ fontFamily: 'var(--disp)', fontSize: 16, fontWeight: 500 }}>Drafts for review</strong>
          <span
            className="statepill"
            style={{
              background: overThreshold ? '#f3e0db' : 'var(--hairline)',
              color: overThreshold ? 'var(--bad)' : 'var(--ink-soft)',
            }}
          >
            {langDrafts.length} / {DRAFT_THRESHOLD}
          </span>
          <div className="grow" />
          <button className="btn pri" disabled={selectedCount === 0 || publishMut.isPending} onClick={publishSelected}>
            {publishMut.isPending ? <Loader2 size={15} className="spin" /> : <UploadCloud size={15} strokeWidth={1.8} />}
            Publish selected{selectedCount > 0 ? ` (${selectedCount})` : ''}
          </button>
        </div>

        <div className="tablewrap">
          <table className="kolectra">
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input type="checkbox" checked={allChecked} onChange={toggleAll} disabled={langDrafts.length === 0} title="Select all" />
                </th>
                <th>Segment</th>
                <th>Domain</th>
                <th>State</th>
                <th style={{ width: 44 }} />
              </tr>
            </thead>
            <tbody>
              {listQuery.isLoading ? (
                <tr>
                  <td colSpan={5} className="mini" style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Loader2 size={14} className="spin" /> Loading…
                  </td>
                </tr>
              ) : listQuery.isError ? (
                <tr>
                  <td colSpan={5} className="mini" style={{ padding: '16px', color: 'var(--bad)' }}>
                    Couldn’t load drafts.
                  </td>
                </tr>
              ) : langDrafts.length === 0 ? (
                <tr>
                  <td colSpan={5} className="mini" style={{ padding: '16px' }}>No drafts in this language yet.</td>
                </tr>
              ) : (
                langDrafts.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <input type="checkbox" checked={selected.has(d.id)} onChange={() => toggle(d.id)} />
                    </td>
                    <td>
                      <span className="id">{d.segmentId}_{d.languoidCode}</span>
                    </td>
                    <td>{d.domain}</td>
                    <td>
                      <span className="statepill sp-DRAFT">Draft</span>
                    </td>
                    <td>
                      <button className="iconbtn" title="View / edit" onClick={() => startEdit(d.id)}>
                        <Pencil strokeWidth={1.8} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function ImportPanel({ teamId, onCommitted }: { teamId: string; onCommitted: () => void }) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [filename, setFilename] = useState<string | null>(null);

  const stageMut = useMutation({
    mutationFn: (file: File) => fileToBase64(file).then((b64) => importApi.stage(teamId, b64, file.name)),
  });
  const commitMut = useMutation({
    mutationFn: (importId: string) => importApi.commit(teamId, importId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['challenges', teamId] });
    },
  });

  function onFile(file: File) {
    setFilename(file.name);
    commitMut.reset();
    stageMut.mutate(file);
  }

  const staged = stageMut.data;
  const errors: RowResult[] = staged ? staged.rows.filter((r) => r.status === 'error') : [];
  // Segments that committing will actually change (new + edited).
  const toApply = staged ? staged.summary.create + staged.summary.overwrite : 0;

  if (commitMut.isSuccess) {
    const d = commitMut.data;
    const parts = [
      d && d.created ? `${d.created} created` : null,
      d && d.updated ? `${d.updated} updated` : null,
      d && d.skipped ? `${d.skipped} unchanged` : null,
      d && d.locked ? `${d.locked} locked` : null,
    ].filter(Boolean);
    return (
      <div className="zone" style={{ textAlign: 'center', padding: 28 }}>
        <CheckCircle2 size={26} strokeWidth={1.8} style={{ color: 'var(--good)', marginBottom: 8 }} />
        <h3 style={{ fontFamily: 'var(--disp)', fontWeight: 500, fontSize: 18, marginBottom: 4 }}>
          Import synced
        </h3>
        <p className="mini" style={{ marginBottom: 14 }}>
          {parts.length ? parts.join(' · ') : 'Nothing to change.'}
          {d && (d.created || d.updated) ? ' — review and publish below.' : ''}
        </p>
        <button className="btn pri" onClick={onCommitted}>Done</button>
      </div>
    );
  }

  return (
    <div>
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx,.csv"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
        }}
      />
      <div className="dropzone" onClick={() => fileRef.current?.click()} style={{ cursor: 'pointer' }}>
        <Upload strokeWidth={1.6} />
        <div className="big">{filename ?? 'Click to choose a spreadsheet'}</div>
        <div className="sub">
          Frontier prep template — one row per utterance. <b>.xlsx</b> is preferred (text with
          commas stays intact); <b>.csv</b> also works.
        </div>
      </div>

      {stageMut.isPending && (
        <div className="banner info" style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Loader2 size={14} className="spin" /> Validating…
        </div>
      )}
      {stageMut.isError && (
        <div className="banner bad" style={{ marginTop: 12 }}>
          {stageMut.error instanceof ApiError ? stageMut.error.message : 'Couldn’t read that file.'}
        </div>
      )}

      {staged && (
        <div style={{ marginTop: 14 }}>
          <div className="toolbar" style={{ marginBottom: 10, flexWrap: 'wrap', gap: 6 }}>
            {staged.summary.create > 0 && (
              <span className="statepill" style={{ background: '#e1efe5', color: 'var(--good)' }}>{staged.summary.create} new</span>
            )}
            {staged.summary.overwrite > 0 && (
              <span className="statepill" style={{ background: '#e6eef7', color: 'var(--data)' }}>{staged.summary.overwrite} changed</span>
            )}
            {staged.summary.skip > 0 && (
              <span className="statepill" style={{ background: 'var(--hairline)', color: 'var(--ink-soft)' }}>{staged.summary.skip} unchanged</span>
            )}
            {staged.summary.locked > 0 && (
              <span className="statepill" style={{ background: '#f7efe0', color: 'var(--warn)' }}>{staged.summary.locked} locked</span>
            )}
            {staged.summary.error > 0 && (
              <span className="statepill" style={{ background: '#f3e0db', color: 'var(--bad)' }}>{staged.summary.error} errors</span>
            )}
            <div className="grow" />
            <button
              className="btn pri"
              disabled={toApply === 0 || commitMut.isPending}
              onClick={() => commitMut.mutate(staged.importId)}
            >
              {commitMut.isPending ? <Loader2 size={15} className="spin" /> : <UploadCloud size={15} strokeWidth={1.8} />}
              {toApply === 0 ? 'Nothing to apply' : `Apply ${toApply} change${toApply === 1 ? '' : 's'}`}
            </button>
          </div>
          {staged.summary.locked > 0 && (
            <div className="banner warn" style={{ marginBottom: 10 }}>
              {staged.summary.locked} segment{staged.summary.locked === 1 ? '' : 's'} changed in the file but ha{staged.summary.locked === 1 ? 's' : 've'} already moved past Draft — left untouched. Rework {staged.summary.locked === 1 ? 'it' : 'them'} first to edit.
            </div>
          )}

          {commitMut.isError && (
            <div className="banner bad" style={{ marginBottom: 10 }}>
              {commitMut.error instanceof ApiError ? commitMut.error.message : 'Commit failed.'}
            </div>
          )}

          {errors.length > 0 && (
            <div className="tablewrap">
              <table className="kolectra">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>Row</th>
                    <th>Segment</th>
                    <th>Problem</th>
                  </tr>
                </thead>
                <tbody>
                  {errors.map((r) => (
                    <tr key={r.row}>
                      <td className="mini">{r.row}</td>
                      <td><span className="id">{r.segmentId || '—'}</span></td>
                      <td className="mini" style={{ color: 'var(--bad)' }}>{r.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ExportPanel({ teamId }: { teamId: string }) {
  const [includeDrafts, setIncludeDrafts] = useState(false);
  const [done, setDone] = useState<{ segments: number; rows: number } | null>(null);

  const exportMut = useMutation({
    mutationFn: () => exportApi.sheet(teamId, includeDrafts),
    onSuccess: (res) => {
      const blob = new Blob([base64ToArrayBuffer(res.fileBase64)], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.filename;
      a.click();
      URL.revokeObjectURL(url);
      setDone({ segments: res.segments, rows: res.rows });
    },
  });

  return (
    <div className="zone" style={{ padding: 18 }}>
      <h3 style={{ fontFamily: 'var(--disp)', fontWeight: 500, fontSize: 18, marginBottom: 4 }}>
        Export a prep sheet
      </h3>
      <p className="mini" style={{ marginBottom: 14 }}>
        Downloads an <b>.xlsx</b> in the exact import format — one row per utterance. Re-import it
        anywhere to sync changes back.
      </p>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={includeDrafts}
          onChange={(e) => {
            setIncludeDrafts(e.target.checked);
            setDone(null);
          }}
        />
        <span>
          Include draft challenges <span className="hint">otherwise published challenges only</span>
        </span>
      </label>

      {exportMut.isError && (
        <div className="banner bad" style={{ marginBottom: 12 }}>
          {exportMut.error instanceof ApiError ? exportMut.error.message : 'Couldn’t generate the export.'}
        </div>
      )}
      {done && (
        <div className="banner info" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <CheckCircle2 size={15} strokeWidth={1.8} style={{ color: 'var(--good)' }} />
          Exported {done.segments} segment{done.segments === 1 ? '' : 's'} ({done.rows} row
          {done.rows === 1 ? '' : 's'}). Check your downloads.
        </div>
      )}

      <button className="btn pri" disabled={exportMut.isPending} onClick={() => exportMut.mutate()}>
        {exportMut.isPending ? <Loader2 size={15} className="spin" /> : <Download size={15} strokeWidth={1.8} />}
        Export {includeDrafts ? 'all' : 'published'} as CSV
      </button>
    </div>
  );
}
